/*
=========================================================
* RingFoods Personalization Section - "Your AI, Your Way"
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
// @mui icons
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import TuneIcon from "@mui/icons-material/Tune";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { accentSuccess } from "pages/RingFoodsHomepage/theme";

const features = [
  {
    icon: RecordVoiceOverIcon,
    title: "Choose Your Restaurant's Voice",
    description:
      "Pick from 10 premium AI voices \u2014 male, female, warm, professional. Find the voice that matches your brand.",
  },
  {
    icon: TuneIcon,
    title: "Teach Your AI Your Restaurant",
    description:
      "Write custom instructions for your AI \u2014 daily specials, upselling strategies, signature dish descriptions. Set different prompts for ordering, reservations, and feedback calls.",
  },
  {
    icon: PhoneForwardedIcon,
    title: "Human Handoff When It Matters",
    description:
      "AI recognizes when a caller needs a real person and transfers the call seamlessly to your team. Your safety net, always ready.",
  },
  {
    icon: VerifiedUserIcon,
    title: "Automatic Regulatory Compliance",
    description:
      "Every call includes legally required AI disclosure \u2014 FCC (US), CRTC (Canada), ICO (UK), ACMA (Australia/NZ). Built in automatically. You never have to think about it.",
  },
];

function Personalization() {
  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      sx={{
        backgroundColor: "#1A1A1A",
      }}
    >
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="white"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Your AI, Your Way
            </MKTypography>
            <MKTypography variant="body1" color="white" sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, opacity: 0.9 }}>
              Customize every detail &mdash; from the voice callers hear to the instructions your AI follows.
            </MKTypography>
          </Grid>
        </Grid>

        {/* 2x2 Feature Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <MKBox
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  {/* Icon */}
                  <MKBox
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 26, color: accentSuccess }} />
                  </MKBox>

                  {/* Title */}
                  <MKTypography variant="h5" color="white" fontWeight={700} sx={{ mb: 1, fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                    {feature.title}
                  </MKTypography>

                  {/* Description */}
                  <MKTypography variant="body2" color="white" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                    {feature.description}
                  </MKTypography>

                  {/* Extra content (voice chips) */}
                  {feature.extra}
                </MKBox>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Personalization;
